import React, { useContext, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import AppContext from 'context/Context';
import { useDispatch } from 'react-redux';
import { getTravelCalendar } from 'redux/booking/travel/travelBookingSlice';
import { TwDropdown } from 'components/common';
import BookingCalendarFrame from '../calendar/BookingCalendarFrame';
import { toCalendarEvent, legendOf } from '../calendar/calendarEvents';
import CalendarEventModal from './booking/CalendarEventModal';

const VIEW_OPTIONS = ['Month View', 'Week View', 'Day View', 'List View', 'Year View'];
const BOOKING_OPTIONS = [
  { id: 'all', name: 'All' },
  { id: 'customer_visit_factory', name: 'Customer visit factory' },
  { id: 'working_at_job_sites', name: 'Working at job sites' },
  { id: 'visit_meeting_with_customer', name: 'Visit/meeting with customer' },
  { id: 'other', name: 'Others' },
];
const VIEW_MAP = { 'Month View': 'dayGridMonth', 'Week View': 'timeGridWeek', 'Day View': 'timeGridDay', 'List View': 'listWeek', default: 'listYear' };

const TravelCalendar = () => {
  const { config: { isRTL } } = useContext(AppContext);
  const calendarRef = useRef();
  const dispatch = useDispatch();

  const [calendarApi, setCalendarApi] = useState(null);
  const [currentFilter, setCurrentFilter] = useState(VIEW_OPTIONS[0]);
  const [currentFilterBooking, setCurrentFilterBooking] = useState(BOOKING_OPTIONS[1]);
  const [title, setTitle] = useState('');
  const [isOpenEventModal, setIsOpenEventModal] = useState(false);
  const [modalEventContent, setModalEventContent] = useState({});
  const [initialEvents, setInitialEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  // Colour = purpose of travel, shown as the event's accent and in the legend.
  const events = useMemo(() => initialEvents.map(toCalendarEvent), [initialEvents]);
  const legend = useMemo(() => legendOf(events, (e) => e.purpose_of_travel), [events]);

  const handleEventClick = useCallback((info) => {
    if (info.event.url) { window.open(info.event.url); info.jsEvent.preventDefault(); }
    else { setModalEventContent(info); setIsOpenEventModal(true); }
  }, []);

  const handleFilter = useCallback((filter) => {
    if (!calendarApi) return;
    setCurrentFilter(filter);
    calendarApi.changeView(VIEW_MAP[filter] || VIEW_MAP.default);
    setTitle(calendarApi.getCurrentData().viewTitle);
  }, [calendarApi]);

  const handleFilterBooking = useCallback((filterId) => {
    setCurrentFilterBooking(BOOKING_OPTIONS.find((item) => item.id === filterId) || BOOKING_OPTIONS[1]);
  }, []);

  const nav = (fn) => { if (!calendarApi) return; calendarApi[fn](); setTitle(calendarApi.getCurrentData().viewTitle); };

  const fetchDataCalendar = useCallback(async (params) => {
    try {
      setLoading(true);
      const response = await dispatch(getTravelCalendar(params)).unwrap();
      setInitialEvents(Array.isArray(response.data) ? response.data.map((item) => item.attributes) : []);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
      setInitialEvents([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (calendarRef.current) {
      const api = calendarRef.current.getApi();
      setCalendarApi(api);
      setTitle(api.getCurrentData().viewTitle);
    }
  }, []);

  useEffect(() => {
    if (!calendarApi?.view) return undefined;
    const timeoutId = setTimeout(() => {
      const view = calendarApi.view;
      fetchDataCalendar({ calendar_type: currentFilterBooking?.id || 'all', from_date: view.activeStart, end_date: view.activeEnd });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [calendarApi, currentFilterBooking, title, fetchDataCalendar]);

  const currentBookingFilterId = currentFilterBooking?.id || BOOKING_OPTIONS[1].id;

  return (
    <div className="tw-p-4">
      <BookingCalendarFrame
        ref={calendarRef}
        title={title}
        loading={loading}
        isRTL={isRTL}
        events={events}
        legend={legend}
        onPrev={() => nav('prev')}
        onNext={() => nav('next')}
        onToday={() => nav('today')}
        onEventClick={handleEventClick}
        filters={(
          <>
            <TwDropdown value={currentBookingFilterId} onChange={handleFilterBooking} options={BOOKING_OPTIONS.map((o) => ({ value: o.id, label: o.name }))} menuWidth={230} />
            <TwDropdown value={currentFilter} onChange={handleFilter} options={VIEW_OPTIONS.map((v) => ({ value: v, label: v }))} menuWidth={160} />
          </>
        )}
      />

      <CalendarEventModal isOpenEventModal={isOpenEventModal} setIsOpenEventModal={setIsOpenEventModal} modalEventContent={modalEventContent} />
    </div>
  );
};

export default React.memo(TravelCalendar);
